/**
 * The live brief, beside the conversation.
 *
 * The mockup's signature element and the reason the intake does not feel like
 * a form: the SME watches the thing they are producing fill in as they talk.
 * Settled paragraphs are body text; the paragraph currently being formed shows
 * as their own draft against a violet rule, replaced the moment the server's
 * extraction lands.
 */

import { ds } from '../ds';
import { RADIUS, SECTIONS, display, monoSmall, panel, EASE } from '../theme';
import type { Brief } from '../api';

interface Props {
  title: string;
  team: string | null;
  brief: Brief;
  /** The composer's current text, shown live under the section being asked
   *  about. Empty while the assistant is talking. */
  draft: string;
}

export function BriefRail({ title, team, brief, draft }: Props) {
  const { Progress } = ds();
  const live = draft.trim();

  return (
    <aside
      style={{
        ...panel,
        position: 'sticky',
        top: 24,
        padding: '28px 28px 24px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={monoSmall}>Live brief</div>
      <h2 style={{ ...display(26), margin: '10px 0 4px' }}>Scoping brief</h2>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-faint)' }}>
        {team ? `${title} · ${team}` : title}
      </div>

      <div
        style={{
          marginTop: 18,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Brief is {brief.progress}% filled in
        </span>
      </div>
      <Progress value={brief.progress} size="sm" tone="signal" />

      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {SECTIONS.map((section) => {
          const paras = brief.sections[section.key] ?? [];
          const capturing = brief.askingAbout === section.key && !!live;
          const empty = paras.length === 0 && !capturing;

          return (
            <div key={section.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={monoSmall}>{section.label}</span>
                {capturing && (
                  <span
                    style={{
                      ...monoSmall,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      color: 'var(--primary-text)'
                    }}
                  >
                    <span
                      style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--primary)' }}
                    />
                    Capturing
                  </span>
                )}
              </div>

              {empty && (
                <div
                  style={{
                    height: 1,
                    background:
                      'repeating-linear-gradient(to right, var(--border-subtle) 0 6px, transparent 6px 12px)'
                  }}
                />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {paras.map((text, i) => (
                  <p
                    key={i}
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: 'var(--text-body)',
                      textWrap: 'pretty',
                      animation: `mvIn .28s ${EASE}`
                    }}
                  >
                    {text}
                  </p>
                ))}
                {capturing && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: 'var(--text-muted)',
                      textWrap: 'pretty',
                      borderLeft: '2px solid var(--violet-300)',
                      paddingLeft: 10
                    }}
                  >
                    {live}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {brief.flags.length > 0 && (
          <div>
            <div style={{ ...monoSmall, marginBottom: 10 }}>Flagged for the meeting</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {brief.flags.map((flag, i) => (
                <div
                  key={i}
                  style={{
                    background: 'var(--surface-inset)',
                    borderRadius: RADIUS.card,
                    padding: '14px 16px',
                    animation: `mvIn .3s ${EASE}`
                  }}
                >
                  <div style={{ ...monoSmall, color: 'var(--accent)', marginBottom: 6 }}>{flag.tag}</div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      color: 'var(--text-strong)',
                      marginBottom: 4,
                      textWrap: 'pretty'
                    }}
                  >
                    {flag.title}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-muted)', textWrap: 'pretty' }}>
                    {flag.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
